import { Icon } from '@shabic/icon';
import { findLast, propEq } from 'ramda';
import { CustomerAddress, ICustomerAddressResponse } from './address.model';
import { IListParams } from './list.model';
import { IProductResponse, Product } from './product.model';
import { Time } from './time.model';
import { TimelinePoint } from './timeline.model';

export type OrderStatus =
  | 'IDLE'
  | 'VERIFYING'
  | 'ACCEPTED_BY_SUPPLIER'
  | 'ACCEPTED_BY_ERP'
  | 'REQUEST_ON_CANCEL'
  | 'CANCEL_APPROVED_BY_SUPPLIER'
  | 'REJECTED_BY_ERP'
  | 'REJECTED_BY_SUPPLIER'
  | 'IN_DELIVERY'
  | 'DELIVERED'
  | 'SENT_BACK'
  | 'SENT_BACK_REPLACE'
  | 'REPLACED'
  | 'REPLACE_APPROVED'
  | 'REPLACE_REJECTED'
  | 'RETURN_APPROVED'
  | 'RETURN_REJECTED'
  | 'RETURNED'
  | 'COMPLETED';

export type OrderState = 'ACTIVE' | 'ARCHIVED';

export const orderStatuses: Array<{
  label: string;
  statuses: OrderStatus[];
}> = [
  { label: 'PENDING', statuses: ['IDLE', 'VERIFYING', 'ACCEPTED_BY_ERP'] },
  { label: 'ACCEPTED', statuses: ['ACCEPTED_BY_SUPPLIER'] },
  { label: 'REQUEST_ON_CANCEL', statuses: ['REQUEST_ON_CANCEL'] },
  {
    label: 'CANCEL_APPROVED_BY_SUPPLIER',
    statuses: ['CANCEL_APPROVED_BY_SUPPLIER'],
  },
  { label: 'REJECTED', statuses: ['REJECTED_BY_SUPPLIER'] },
  { label: 'IN_DELIVERY', statuses: ['IN_DELIVERY'] },
  { label: 'DELIVERED', statuses: ['DELIVERED'] },
  { label: 'SENT_BACK', statuses: ['SENT_BACK', 'SENT_BACK_REPLACE'] },
  { label: 'REPLACED', statuses: ['REPLACED'] },
  { label: 'REPLACE_APPROVED', statuses: ['REPLACE_APPROVED'] },
  { label: 'REPLACE_REJECTED', statuses: ['REPLACE_REJECTED'] },
  { label: 'RETURN_APPROVED', statuses: ['RETURN_APPROVED'] },
  { label: 'RETURN_REJECTED', statuses: ['RETURN_REJECTED'] },
  { label: 'RETURNED', statuses: ['RETURNED'] },
  { label: 'COMPLETED', statuses: ['COMPLETED'] },
];

export const orderIcons: Record<OrderStatus, Icon> = {
  IDLE: 'check',
  VERIFYING: 'check',
  ACCEPTED_BY_SUPPLIER: 'check',
  ACCEPTED_BY_ERP: 'check',
  REQUEST_ON_CANCEL: 'check',
  CANCEL_APPROVED_BY_SUPPLIER: 'check',
  REJECTED_BY_ERP: 'close',
  REJECTED_BY_SUPPLIER: 'close',
  COMPLETED: 'check',
  DELIVERED: 'check',
  SENT_BACK: 'check',
  SENT_BACK_REPLACE: 'check',
  REPLACED: 'check',
  REPLACE_APPROVED: 'check',
  REPLACE_REJECTED: 'close',
  RETURN_APPROVED: 'check',
  RETURN_REJECTED: 'close',
  RETURNED: 'check',
  IN_DELIVERY: 'check',
};

interface IOrderPrice {
  price: number;
  vat: number;
  discount: number;
}

export interface IPreOrderResponse extends IOrderPrice {
  id: number;
  quotationId: string;
  quotationExpirationDate: string;
  quantity: number;
  createdDate: string;
  lastModifiedDate: string;
  product: IProductResponse;
}

export class PreOrder {
  id: number;
  product: Product;
  quotationId: string;
  quotationExpirationDate: Time;
  quantity: number;
  price: number;
  vat: number;
  discount: number;
  createdDate: Time;
  lastModifiedDate: Time;
  // Track the actual currency of the quotation price
  // Quotation API returns prices in SAR regardless of product currency
  quotationCurrency: string = 'SAR';

  constructor(payload: IPreOrderResponse) {
    this.product = new Product(payload.product);
    this.quotationId = payload.quotationId;
    this.quotationExpirationDate = new Time(payload.quotationExpirationDate);
    this.quantity = payload.quantity;
    this.price = payload.price;
    this.vat = payload.vat;
    this.discount = payload.discount;
    this.createdDate = new Time(payload.createdDate);
    this.lastModifiedDate = new Time(payload.lastModifiedDate);
    this.id = payload.id;
    
    // NOTE: Quotation API returns prices in SAR, so we use SAR as the quotation currency
    // This prevents incorrect currency conversion when product.currency is set to USD
  }

  getTotal() {
    return (this.price + this.vat - this.discount) * this.quantity;
  }

  isExpired() {
    return this.quotationExpirationDate.isPast();
  }
}

export interface IOrderPayload {
  shippingAddressId: number;
  receiverFirstName: string;
  receiverLastName: string;
  receiverEmail: string;
  receiverPhone: string;
  deliveryNotes: string;
  products: Array<{
    productId: string;
    quotationId: string;
    quantity: number;
  }>;
}

type OrderHistoryResponse = Record<OrderStatus, string>;

export interface IOrderResponse {
  id: number;
  status: OrderStatus;
  supplierState: OrderState;
  buyerState: OrderState;
  companyId: number;
  buyerCompanyId: number;
  supplierCompanyId: number;
  createdDate: string;
  shippingAddress: ICustomerAddressResponse;
  reason?: Record<OrderStatus, string>;
  history?: OrderHistoryResponse;
  products: Array<
    {
      quotationId: string;
      quantity: number;
      replaceQuantity?: number;
      returnQuantity?: number;
      product: IProductResponse;
    } & IOrderPrice
  >;
  groupId: string;
  receiverFirstName: string;
  receiverLastName: string;
  receiverEmail: string;
  receiverPhone: string;
  deliveryNotes?: string;
  orderMedias?: Array<{
    id: number;
    mediaId: number;
    mediaUrl: string;
    mediaType: string;
    type: string;
    description: string;
    mediaOrder: number;
    createdDate: string;
  }>;
}

interface IOrderProducts extends IOrderPrice {
  quotationId: string;
  quantity: number;
  replaceQuantity?: number;
  returnQuantity?: number;
  product: Product;
}

export interface IOrderParams extends IListParams {
  forSupplier: boolean;
}

export class Order {
  id: number;
  createdDate: Time;
  status: OrderStatus;
  supplierState: OrderState;
  buyerState: OrderState;
  products: IOrderProducts[];
  address: CustomerAddress;
  companyId: number;
  reason: Map<OrderStatus, string>;
  groupId: string;
  history: TimelinePoint[];
  receiver: OrderReceiver;
  orderMedias?: Array<{
    id: number;
    mediaId: number;
    mediaUrl: string;
    mediaType: string;
    type: string;
    description: string;
    mediaOrder: number;
    createdDate: string;
  }>;

  constructor(payload: IOrderResponse, forSupplier: boolean = false) {
    this.id = payload.id;
    this.status = payload.status;
    this.createdDate = new Time(payload.createdDate);
    this.address = new CustomerAddress(payload.shippingAddress);
    this.companyId = forSupplier ? payload.buyerCompanyId : payload.companyId;
    this.products = payload.products.map(el => {
      return {
        ...el,
        product: new Product(el.product),
      };
    });
    this.reason = this.parseReasons(payload.reason);
    this.supplierState = payload.supplierState;
    this.buyerState = payload.buyerState;
    this.groupId = payload.groupId;
    this.history = this.parseHistory(payload.history);
    this.orderMedias = payload.orderMedias;
    this.receiver = new OrderReceiver({
      firstName: payload.receiverFirstName,
      lastName: payload.receiverLastName,
      email: payload.receiverEmail,
      phone: payload.receiverPhone,
      notes: payload.deliveryNotes,
    });
  }

  get isPending() {
    return ['IDLE', 'VERIFYING', 'ACCEPTED_BY_ERP'].includes(this.status);
  }

  get isAccepted() {
    return this.status === 'ACCEPTED_BY_SUPPLIER';
  }

  get isRejected() {
    return this.status === 'REJECTED_BY_SUPPLIER';
  }

  get isCompleted() {
    return this.status === 'COMPLETED';
  }

  get isDelivered() {
    return this.status === 'DELIVERED';
  }

  get inDelivery() {
    return this.status === 'IN_DELIVERY';
  }

  get isSentBack() {
    return this.status === 'SENT_BACK' || this.status === 'SENT_BACK_REPLACE';
  }

  get isReturned() {
    return this.status === 'RETURNED';
  }

  get isReplaced() {
    return this.status === 'REPLACED';
  }

  get isReturnApproved() {
    return this.status === 'RETURN_APPROVED';
  }

  get isReturnRejected() {
    return this.status === 'RETURN_REJECTED';
  }

  get isReplaceApproved() {
    return this.status === 'REPLACE_APPROVED';
  }

  get isReplaceRejected() {
    return this.status === 'REPLACE_REJECTED';
  }

  canBeArchived(role: 'buyer' | 'supplier') {
    const allowStatuses: OrderStatus[] = [
      'COMPLETED',
      'REJECTED_BY_SUPPLIER',
      'REJECTED_BY_ERP',
      'CANCEL_APPROVED_BY_SUPPLIER',
      'RETURNED',
      'REPLACED',
    ];
    const state = role === 'buyer' ? this.buyerState : this.supplierState;

    return state !== 'ARCHIVED' && allowStatuses.includes(this.status);
  }

  statusIs(status: OrderStatus) {
    return this.status === status;
  }

  getReasonForStatus(status: OrderStatus): string | undefined {
    return this.reason.get(status);
  }

  getReturnReason(): string | undefined {
    return this.getReasonForStatus('SENT_BACK') || this.getReasonForStatus('SENT_BACK_REPLACE') || this.getReasonForStatus('RETURNED');
  }

  isSentBackForReplacement(): boolean {
    return this.status === 'SENT_BACK_REPLACE' || 
           (this.status === 'SENT_BACK' && (this.getReasonForStatus('SENT_BACK')?.toLowerCase().includes('replace') || false));
  }

  isSentBackForReturn(): boolean {
    return this.status === 'SENT_BACK' && !this.isSentBackForReplacement();
  }

  getReplacementQuantities(): Array<{ productId: string; productName: string; originalQuantity: number; replacementQuantity: number }> {
    if (!this.isSentBackForReplacement()) {
      return [];
    }

    return this.products.map(product => ({
      productId: product.product.id || '',
      productName: product.product.name.get('en') || '',
      originalQuantity: product.quantity,
      replacementQuantity: product.replaceQuantity || product.quantity // Use replaceQuantity from backend, fallback to original quantity
    }));
  }

  getTotalReplacementQuantity(): number {
    return this.getReplacementQuantities().reduce((total, item) => total + item.replacementQuantity, 0);
  }

  getOrderMedias(): Array<{
    id: number;
    mediaId: number;
    mediaUrl: string;
    mediaType: string;
    type: string;
    description: string;
    mediaOrder: number;
    createdDate: string;
  }> {
    return this.orderMedias || [];
  }

  getReturnDate(): Time | undefined {
    // Check if we have history data
    if (!this.history || this.history.length === 0) {
      return undefined;
    }

    // Find the RETURNED timeline point
    const returnedPoint = this.history.find(point => point.id === 'RETURNED');
    if (returnedPoint?.completed && returnedPoint.date) {
      return returnedPoint.date;
    }

    // If no RETURNED point, check for SENT_BACK point (when return was initiated)
    const sentBackPoint = this.history.find(point => point.id === 'SENT_BACK');
    if (sentBackPoint?.completed && sentBackPoint.date) {
      return sentBackPoint.date;
    }

    return undefined;
  }

  getReplaceDate(): Time | undefined {
    // Check if we have history data
    if (!this.history || this.history.length === 0) {
      return undefined;
    }

    // Find the REPLACED timeline point
    const replacedPoint = this.history.find(point => point.id === 'REPLACED');
    if (replacedPoint?.completed && replacedPoint.date) {
      return replacedPoint.date;
    }

    // If no REPLACED point, check for SENT_BACK_REPLACE point (when replacement was initiated)
    const sentBackReplacePoint = this.history.find(point => point.id === 'SENT_BACK_REPLACE');
    if (sentBackReplacePoint?.completed && sentBackReplacePoint.date) {
      return sentBackReplacePoint.date;
    }

    return undefined;
  }

  totalPrice() {
    return this.products.reduce((prev, curr) => {
      // Calculate base total for this product
      const baseTotal = (curr.price - curr.discount) * curr.quantity;
      
      // Prevent negative totals - if discount exceeds price, cap it
      if (baseTotal < 0) {
        const maxDiscount = curr.price;
        const adjustedDiscount = Math.min(curr.discount, maxDiscount);
        return prev + (curr.price - adjustedDiscount) * curr.quantity;
      }
      
      return prev + baseTotal;
    }, 0);
  }

  totalVat() {
    return this.products.reduce((prev, curr) => {
      return prev + curr.vat * curr.quantity;
    }, 0);
  }

  private parseReasons(reason?: Record<OrderStatus, string>) {
    const map = new Map<OrderStatus, string>();

    for (const status in reason) {
      const key = status as OrderStatus;

      map.set(key, reason[key]);
    }

    return map;
  }

  private parseHistory(history?: OrderHistoryResponse) {
    // Base timeline points for normal delivery flow
    const basePoints: Array<{ status: OrderStatus; icon: Icon }> = [
      { status: 'ACCEPTED_BY_SUPPLIER', icon: 'icon-home' },
      { status: 'IN_DELIVERY', icon: 'icon-home' },
      { status: 'DELIVERED', icon: 'icon-home' },
      { status: 'COMPLETED', icon: 'icon-home' },
    ];

    // Return flow points
    const returnPoints: Array<{ status: OrderStatus; icon: Icon }> = [
      { status: 'SENT_BACK', icon: 'icon-home' },
      { status: 'RETURN_APPROVED', icon: 'icon-home' },
      { status: 'RETURN_REJECTED', icon: 'icon-home' },
      { status: 'RETURNED', icon: 'icon-home' },
    ];

    // Replacement flow points
    const replacePoints: Array<{ status: OrderStatus; icon: Icon }> = [
      { status: 'SENT_BACK_REPLACE', icon: 'icon-home' },
      { status: 'REPLACE_APPROVED', icon: 'icon-home' },
      { status: 'REPLACE_REJECTED', icon: 'icon-home' },
      { status: 'REPLACED', icon: 'icon-home' },
    ];

    // Determine which flow to show based on order status
    let pointsToShow: Array<{ status: OrderStatus; icon: Icon }> = [...basePoints];

    // Check if order has return history (more specific check)
    const hasReturnHistory = history && (
      history['SENT_BACK'] || 
      history['RETURN_APPROVED'] || 
      history['RETURN_REJECTED'] || 
      history['RETURNED']
    );

    // Check if order has replacement history (more specific check)
    const hasReplaceHistory = history && (
      history['SENT_BACK_REPLACE'] || 
      history['REPLACE_APPROVED'] || 
      history['REPLACE_REJECTED'] || 
      history['REPLACED']
    );

    // Determine which flow to show - prioritize return over replacement
    if (hasReturnHistory) {
      // Show return flow - ONLY return points, no replacement
      pointsToShow = [
        { status: 'ACCEPTED_BY_SUPPLIER', icon: 'icon-home' },
        { status: 'IN_DELIVERY', icon: 'icon-home' },
        { status: 'DELIVERED', icon: 'icon-home' },
        { status: 'SENT_BACK', icon: 'icon-home' },
      ];

      // Add return-specific points based on actual history
      if (history?.['RETURN_APPROVED']) {
        pointsToShow.push({ status: 'RETURN_APPROVED', icon: 'icon-home' });
        if (history?.['RETURNED']) {
          pointsToShow.push({ status: 'RETURNED', icon: 'icon-home' });
        }
      } else if (history?.['RETURN_REJECTED']) {
        pointsToShow.push({ status: 'RETURN_REJECTED', icon: 'icon-home' });
        // COMPLETED will be added later in the logic
      } else {
        // Return is pending - show both options
        pointsToShow.push({ status: 'RETURN_APPROVED', icon: 'icon-home' });
        pointsToShow.push({ status: 'RETURN_REJECTED', icon: 'icon-home' });
      }
      
      // Remove COMPLETED from return flow (will be added back if needed)
      pointsToShow = pointsToShow.filter(point => point.status !== 'COMPLETED');
    } else if (hasReplaceHistory) {
      // Show replacement flow - ONLY replacement points, no return
      pointsToShow = [
        { status: 'ACCEPTED_BY_SUPPLIER', icon: 'icon-home' },
        { status: 'IN_DELIVERY', icon: 'icon-home' },
        { status: 'DELIVERED', icon: 'icon-home' },
        { status: 'SENT_BACK_REPLACE', icon: 'icon-home' },
      ];

      // Add replacement-specific points based on actual history
      if (history?.['REPLACE_APPROVED']) {
        pointsToShow.push({ status: 'REPLACE_APPROVED', icon: 'icon-home' });
        if (history?.['REPLACED']) {
          pointsToShow.push({ status: 'REPLACED', icon: 'icon-home' });
        }
      } else if (history?.['REPLACE_REJECTED']) {
        pointsToShow.push({ status: 'REPLACE_REJECTED', icon: 'icon-home' });
        // COMPLETED will be added later in the logic
      } else {
        // Replacement is pending - show both options
        pointsToShow.push({ status: 'REPLACE_APPROVED', icon: 'icon-home' });
        pointsToShow.push({ status: 'REPLACE_REJECTED', icon: 'icon-home' });
      }
      
      // Remove COMPLETED from replacement flow (will be added back if needed)
      pointsToShow = pointsToShow.filter(point => point.status !== 'COMPLETED');
    }

    let timelinePoints = pointsToShow.map(({ status, icon }) => {
      const point = new TimelinePoint(status, `status.${status}`, icon);

      history?.[status] && point.complete(history?.[status]);

      return point;
    });

    // For return flow, handle COMPLETED status for rejected returns
    if (hasReturnHistory && history?.['RETURN_REJECTED']) {
      // Add COMPLETED back to the timeline for rejected returns
      timelinePoints.push(new TimelinePoint('COMPLETED', 'status.COMPLETED', 'icon-home'));
      if (history?.['COMPLETED']) {
        timelinePoints[timelinePoints.length - 1].complete(history['COMPLETED']);
      }
    }

    // For replacement flow, handle COMPLETED status for rejected replacements
    if (hasReplaceHistory && history?.['REPLACE_REJECTED']) {
      // Add COMPLETED back to the timeline for rejected replacements
      timelinePoints.push(new TimelinePoint('COMPLETED', 'status.COMPLETED', 'icon-home'));
      if (history?.['COMPLETED']) {
        timelinePoints[timelinePoints.length - 1].complete(history['COMPLETED']);
      }
    }

    const lastActivated = findLast<TimelinePoint>(propEq('completed', true))(
      timelinePoints
    );

    lastActivated?.activate();

    return timelinePoints;
  }
}

export class OrderBulk {
  groupId: string;
  orders: Order[];

  constructor(payload: {
    orders: IOrderResponse[];
    groupId: string;
    forSupplier: boolean;
  }) {
    this.groupId = payload.groupId;
    this.orders = payload.orders.map(el => new Order(el, payload.forSupplier));
  }
}

export interface IOrderReceiver {
  firstName: string | null | undefined;
  lastName: string | null | undefined;
  email: string | null | undefined;
  phone: string | null | undefined;
  notes?: string | null | undefined;
}

export class OrderReceiver {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  notes?: string;

  constructor(payload: IOrderReceiver) {
    this.firstName = payload.firstName || '';
    this.lastName = payload.lastName || '';
    this.email = payload.email || '';
    this.phone = payload.phone || '';
    this.notes = payload.notes || '';
  }
}
